package org.example.project.enginetabular

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object TabularManager {
    private val SERVER_URL: String
        get() = window.location.origin

    private val json = Json {
        prettyPrint = true
        ignoreUnknownKeys = true
    }

    private fun headers() = LumaticaApiManager.auditHeaders("application/json")

    private fun resolveUserId(userId: String): String =
        userId.ifBlank { LoginService.getId() }

    fun sanitizeTableId(input: String): String {
        val cleaned = input
            
            .lowercase()
            .replace(Regex("[^a-z0-9]+"), "_")
            .replace(Regex("_+"), "_")
            .trim('_')
        return cleaned.ifBlank { "tabelle" }
    }

    private fun normalizeColumnKey(label: String, fallbackIndex: Int): String {
        val normalized = sanitizeTableId(label)
        return if (normalized == "tabelle") "spalte_${fallbackIndex + 1}" else normalized
    }

    fun createEmptyTable(
        name: String,
        headers: List<String> = emptyList(),
        columnTypes: List<String> = emptyList()
    ): All {
        val normalizedName = name.ifBlank { "Neue Tabelle" }
        val columns = headers.mapIndexed { index, header ->
            All(
                key = normalizeColumnKey(header, index),
                label = header.ifBlank { "Spalte ${index + 1}" },
                typ = columnTypes.getOrNull(index) ?: "TEXT"
            )
        }
        return All(
            id = sanitizeTableId(normalizedName),
            name = normalizedName,
            columnSettings = normalizeColumns(columns)
        )
    }

    fun normalizeColumns(columns: List<All>): List<All> {
        val usedKeys = mutableSetOf<String>()
        return columns.mapIndexed { index, column ->
            val label = column.label
            var key = column.key.ifBlank { normalizeColumnKey(label, index) }
            while (!usedKeys.add(key)) {
                key = "${key}_${index + 1}"
            }
            column.copy(key = key, label = label)
        }
    }

    fun normalizeRows(rows: List<All>, columnCount: Int): List<All> =
        rows.mapIndexed { index, row ->
            val rowId = row.id.ifBlank { (index + 1).toString() }
            val values = MutableList(columnCount) { col -> row.columns.getOrNull(col).orEmpty() }
            row.copy(id = rowId, columns = values)
        }

    fun toTabularScreenData(document: All): List<Pair<String, List<String>>> =
        normalizeRows(document.module, document.columnSettings.size).map { it.id to it.columns }

    fun withCommittedRows(
        document: All,
        committedRows: List<Pair<String, List<String>>>
    ): All {
        val normalizedColumns = normalizeColumns(document.columnSettings)
        return document.copy(
            columnSettings = normalizedColumns,
            module = normalizeRows(
                committedRows.map { (id, values) -> All(id = id, columns = values) },
                normalizedColumns.size
            )
        )
    }

    suspend fun loadUserTables(userId: String = ""): List<All> {
        val uid = resolveUserId(userId)
        if (uid.isBlank()) return emptyList()
        return try {
            val raw = LumaticaApiManager.call("tabularcreator/list/${apiPart(uid)}")
            if (raw.isBlank() || raw == "[]") return emptyList()
            json.decodeFromString(
                ListSerializer(All.serializer()),
                raw
            ).map { normalizeLoadedDocument(it) }
        } catch (_: Throwable) {
            emptyList()
        }
    }

    suspend fun loadUserTable(tableIdOrName: String, userId: String = ""): All? {
        val uid = resolveUserId(userId)
        val tableId = sanitizeTableId(tableIdOrName)
        if (uid.isBlank() || tableId.isBlank()) return null
        return try {
            val raw = LumaticaApiManager.call("tabularcreator/load/${apiPart(uid)}/${apiPart(tableId)}")
            if (raw.isBlank() || raw == "[]") return null
            normalizeLoadedDocument(json.decodeFromString(All.serializer(), raw))
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun saveUserTable(document: All, userId: String = ""): All? {
        val uid = resolveUserId(userId)
        if (uid.isBlank()) return null
        val existing = if (document.id.isBlank()) null else loadUserTable(document.id, uid)
        val normalizedName = document.name.ifBlank { existing?.name ?: "Neue Tabelle" }
        val tableId = sanitizeTableId(document.id.ifBlank { normalizedName })
        val normalizedColumns = normalizeColumns(document.columnSettings)
        val dokumentEintrag = document.copy(
            id = tableId,
            name = normalizedName,
            columnSettings = normalizedColumns,
            module = normalizeRows(document.module, normalizedColumns.size)
        )

        return try {
            val res = window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/tabularcreator/save-json/${apiPart(uid)}/${apiPart(tableId)}",
                RequestInit(
                    method = "POST",
                    headers = headers(),
                    body = json.encodeToString(All.serializer(), dokumentEintrag)
                )
            ).await()
            if (!res.ok) return null
            normalizeLoadedDocument(json.decodeFromString(All.serializer(), res.text().await()))
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun deleteUserTable(tableIdOrName: String, userId: String = ""): Boolean {
        val uid = resolveUserId(userId)
        val tableId = sanitizeTableId(tableIdOrName)
        if (uid.isBlank() || tableId.isBlank()) return false
        return try {
            LumaticaApiManager.call("tabularcreator/delete/${apiPart(uid)}/${apiPart(tableId)}").contains("\"ok\":true")
        } catch (_: Throwable) {
            false
        }
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(value.ifBlank { "~" }) as String

    private fun normalizeLoadedDocument(document: All): All {
        val normalizedName = document.name.ifBlank { document.id.ifBlank { "Tabelle" } }
        val normalizedColumns = normalizeColumns(document.columnSettings)
        return document.copy(
            id = sanitizeTableId(document.id.ifBlank { normalizedName }),
            name = normalizedName,
            columnSettings = normalizedColumns,
            module = normalizeRows(document.module, normalizedColumns.size)
        )
    }
}
